import React from "react";
import useSeo from "../hooks/useSeo";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, UserPlus, ShieldCheck, Send, Check, Search } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { CAREGIVER_IMAGE } from "../mock";

const STEPS = [
  { icon: UserPlus, t: "Create your free profile", d: "Sign up as a caregiver in minutes — add your experience, the services you offer and your availability. It's completely free." },
  { icon: ShieldCheck, t: "Get verified by our team", d: "Upload your photo ID and resume. We personally review every caregiver so families can hire with confidence — you're notified as soon as you're approved." },
  { icon: Send, t: "Apply to jobs you love", d: "Once approved, browse open jobs in your area from your dashboard and apply in a few clicks. Families contact you directly." },
];

const PERKS = [
  "100% free for caregivers — no fees, ever",
  "Work with vetted, respectful families",
  "Choose jobs that fit your schedule and location",
  "Nanny, babysitter, housekeeper or senior care",
  "Apply in a few clicks from your dashboard",
  "Personal support whenever you need it",
];

export default function ForCaregivers() {
  const navigate = useNavigate();
  useSeo("Nanny, Babysitter & Caregiver Jobs | SimplySitter", "Find flexible caregiving jobs near you. Apply to nanny, babysitter, housekeeper and senior care jobs from trusted families on SimplySitter.");
  const go = () => navigate("/auth?role=caregiver");

  return (
    <div>
      <Header />
      <section className="hero-gradient">
        <div className="max-w-7xl mx-auto px-5 pt-14 pb-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="order-2 lg:order-1">
            <img src={CAREGIVER_IMAGE} alt="Caregiver playing with a child at home" className="w-full h-[440px] object-cover rounded-[28px] shadow-[0_30px_70px_-25px_rgba(31,26,27,0.4)]" />
          </div>
          <div className="order-1 lg:order-2">
            <h1 className="font-display text-[2.6rem] leading-[1.05] sm:text-6xl text-[#1f1a1b]">Find caregiving jobs that fit you</h1>
            <p className="mt-5 text-[18px] leading-relaxed text-[#5a5254] max-w-xl">
              Whether nanny, babysitter, household help or senior care — create your profile, discover matching jobs in your region and apply in a few clicks. Free.
            </p>
            <button onClick={go} className="inline-flex items-center gap-2 mt-8 rounded-xl bg-[#7c2233] hover:bg-[#661b29] text-white font-medium px-8 py-4 shadow-sm transition-colors">
              Get started as a caregiver <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-3 text-[14px] text-[#8a8285]">Already have an account? <Link to="/auth?mode=login" className="text-[#7c2233] font-medium hover:underline">Log in</Link></p>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-5xl mx-auto px-5 py-16">
          <h2 className="font-display text-3xl sm:text-4xl text-[#1f1a1b] text-center">How it works for caregivers</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.t} className="rounded-2xl border border-black/[0.07] p-7">
                <div className="w-12 h-12 rounded-xl bg-[#f7e4e6] flex items-center justify-center"><s.icon className="w-6 h-6 text-[#7c2233]" strokeWidth={1.7} /></div>
                <div className="font-display text-lg text-[#7c2233] mt-4">Step {i + 1}</div>
                <h3 className="font-display text-xl text-[#1f1a1b] mt-1">{s.t}</h3>
                <p className="text-[15px] text-[#5a5254] mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#fdfaf7]">
        <div className="max-w-4xl mx-auto px-5 py-16">
          <div className="rounded-3xl bg-white border border-black/[0.07] p-8 sm:p-12">
            <span className="inline-block text-[13px] font-medium text-[#7c2233] bg-[#f7e4e6] rounded-full px-3 py-1">Free for caregivers</span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#1f1a1b] mt-4">Why caregivers choose SimplySitter</h2>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-6">
              {PERKS.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-[15px] text-[#3a3436]">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[#7c2233] flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-white" strokeWidth={3} /></span>{p}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={go} className="rounded-xl bg-[#7c2233] hover:bg-[#661b29] text-white font-medium px-7 py-3.5 transition-colors">Create free profile</button>
              <Link to="/jobs" className="inline-flex items-center gap-2 rounded-xl border border-[#7c2233]/30 text-[#7c2233] font-medium px-7 py-3.5 hover:bg-[#faf1f2] transition-colors"><Search className="w-4 h-4" /> Browse open jobs</Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
